import React, { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import JsonLd from "./JsonLd";

/* HomeFaq
   --------
   Six homepage-focused questions covering course, free tools, schools
   and BSL basics. Accordion-style - only one open at a time keeps the
   block compact. Uses native <button> + ARIA, no third-party deps.

   Also emits a Schema.org FAQPage block so Google can attach the same
   Q&A pairs as rich snippets in the SERP.
*/

const FAQS = [
  {
    q: "Is the online BSL course suitable for complete beginners?",
    plain: "Yes. The course starts with the BSL fingerspelling alphabet and introduces signs gradually. Each sign and phrase is shown on video, and there’s an assessment at the end of every section. No prior signing experience needed.",
    a: (
      <>
        Yes. The course starts with the BSL fingerspelling alphabet and
        introduces signs gradually. Each sign and phrase is shown on video,
        and there’s an assessment at the end of every section. No prior
        signing experience needed.
      </>
    ),
  },
  {
    q: "How much does the course cost?",
    plain: "Pay What You Can, from just £5. It’s a one-time payment, not a subscription, and you keep access for two years.",
    a: (
      <>
        Pay What You Can, from just &pound;5. It&rsquo;s a one-off
        payment, not a subscription, and you keep access for two years.
      </>
    ),
  },
  {
    q: "Are the free BSL tools really free?",
    plain: "Yes. The dictionary, Sign of the Day, sign quiz, fingerspelling player, printables and embeddable widgets are all free with no sign-up.",
    a: (
      <>
        Yes. The dictionary, Sign of the Day, sign quiz, fingerspelling
        player, printables and embeddable widgets are all free with no
        sign-up.
      </>
    ),
  },
  {
    q: "Can schools and groups use the printables?",
    plain: "Absolutely. The printable resources are free to download and use in classrooms, libraries and group sessions. See the lesson plans section for ready-made materials and ideas.",
    a: (
      <>
        Absolutely. The printable resources are free to download and use
        in classrooms, libraries and group sessions. See the{" "}
        <a href="/lesson-plans">lesson plans</a> section for ready-made
        materials and ideas.
      </>
    ),
  },
  {
    q: "Do I receive a certificate?",
    plain: "Yes. You receive a certificate of completion at the end of the online course.",
    a: (
      <>
        Yes. You receive a certificate of completion at the
        end of the online course.
      </>
    ),
  },
  {
    q: "Where can I look up a BSL sign?",
    plain: "Use the BSL dictionary. Search any word and you’ll get a clear image or video of the sign, plus related signs.",
    a: (
      <>
        Use the <a href="/bsl-dictionary">BSL dictionary</a>. Search any
        word and you&rsquo;ll get a clear image or video of the sign,
        plus related signs.
      </>
    ),
  },
];

const HomeFaq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="bs-faq" aria-label="Frequently asked questions" data-testid="home-faq">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.plain },
        })),
      }} />
      <div className="bs-container">
        <header className="bs-faq__head">
          <span className="bs-eyebrow"><HelpCircle size={14} aria-hidden="true" /> Quick answers</span>
          <h2 className="bs-faq__title">Frequently asked questions</h2>
        </header>

        <ul className="bs-faq__list">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <li key={i} className={`bs-faq__item ${isOpen ? "is-open" : ""}`}>
                <button
                  type="button"
                  className="bs-faq__q"
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-q-${i}`}
                >
                  <span>{item.q}</span>
                  <ChevronDown size={18} aria-hidden="true" className="bs-faq__chev" />
                </button>
                {isOpen && (
                  <div id={`faq-panel-${i}`} className="bs-faq__a" role="region">
                    <p>{item.a}</p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default HomeFaq;
